import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  File,
  Folder,
  Download,
  Search,
  RefreshCw,
  ChevronRight,
  Home,
  ArrowLeft,
  Loader2,
  HardDrive,
} from 'lucide-react'
import styles from '../AdminDashboard/AdminDashboard.module.scss'
import dayjs from 'dayjs'
import { message } from 'antd'
import { config } from '../../config'

interface Artifact {
  name: string
  type: 'file' | 'directory'
  mtime: string
  size?: number
}

const formatSize = (bytes?: number) => {
  if (bytes === undefined) return '-'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`
}

const Artifacts: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>('')
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [displayLimit, setDisplayLimit] = useState<number>(10)

  const fetchArtifacts = useCallback(async (path: string) => {
    try {
      setLoading(true)
      // Ensure path starts and ends with / correctly
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const fetchUrl = `${config.ARTIFACTS_URL}${normalizedPath}${normalizedPath.endsWith('/') ? '' : '/'}`

      const response = await fetch(fetchUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch artifacts: ${response.statusText}`)
      }

      const text = await response.text()
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(text, 'text/xml')

      const items: Artifact[] = []

      // Parse directories
      const directories = xmlDoc.getElementsByTagName('directory')
      for (let i = 0; i < directories.length; i++) {
        const dir = directories[i]
        items.push({
          name: dir.textContent || '',
          type: 'directory',
          mtime: dir.getAttribute('mtime') || '',
        })
      }

      // Parse files
      const files = xmlDoc.getElementsByTagName('file')
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        items.push({
          name: file.textContent || '',
          type: 'file',
          mtime: file.getAttribute('mtime') || '',
          size: parseInt(file.getAttribute('size') || '0', 10),
        })
      }

      // Sort: directories first, then files, both alphabetically
      items.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'directory' ? -1 : 1
        }
        return a.name.localeCompare(b.name)
      })

      setArtifacts(items)
      setDisplayLimit(10)
    } catch (error) {
      console.error('Error fetching artifacts:', error)
      message.error('Failed to load artifacts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchArtifacts(currentPath)
  }, [currentPath, fetchArtifacts])

  const filteredArtifacts = useMemo(() => {
    if (!searchQuery.trim()) return artifacts

    const query = searchQuery.toLowerCase().trim()
    const parts = query.split(/\s+/)

    return artifacts.filter((item) => {
      const name = item.name.toLowerCase()
      // Basic fuzzy: all parts of the query must be present in the name
      return parts.every((part) => name.includes(part))
    })
  }, [artifacts, searchQuery])

  const visibleArtifacts = filteredArtifacts.slice(0, displayLimit)
  const hasMore = displayLimit < filteredArtifacts.length

  const handleDirectoryClick = (dirName: string) => {
    const newPath = currentPath === '' ? dirName : `${currentPath}/${dirName}`
    setCurrentPath(newPath)
    setSearchQuery('')
  }

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setCurrentPath('')
    } else {
      const pathParts = currentPath.split('/')
      const newPath = pathParts.slice(0, index + 1).join('/')
      setCurrentPath(newPath)
    }
    setSearchQuery('')
  }

  const handleDownload = (fileName: string) => {
    const normalizedPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`
    const downloadUrl = `${config.ARTIFACTS_URL}${normalizedPath}${normalizedPath.endsWith('/') ? '' : '/'}${fileName}`
    window.open(downloadUrl, '_blank')
  }

  const pathParts = currentPath === '' ? [] : currentPath.split('/')

  return (
    <div className={styles.managerContainer}>
      <div className={styles.managerHeader}>
        <div className={styles.headerLeft}>
          <h2>Artifacts</h2>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: 'var(--color-text-muted)',
            }}>
            <span
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => handleBreadcrumbClick(-1)}>
              <Home size={14} />
            </span>
            {pathParts.map((part, index) => (
              <React.Fragment key={index}>
                <ChevronRight size={12} />
                <span
                  style={{
                    cursor: index < pathParts.length - 1 ? 'pointer' : 'default',
                    fontWeight: index === pathParts.length - 1 ? 600 : 400,
                    color: index === pathParts.length - 1 ? 'var(--color-text-primary)' : 'inherit',
                  }}
                  onClick={() => index < pathParts.length - 1 && handleBreadcrumbClick(index)}>
                  {part}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className={styles.headerActions}>
          <div className={styles.filterInput}>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setDisplayLimit(10)
              }}
            />
          </div>
          <button className={styles.refreshButton} onClick={() => fetchArtifacts(currentPath)} disabled={loading}>
            <RefreshCw size={14} className={loading ? styles.spinner : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className={styles.tableContainer}>
        {loading && artifacts.length === 0 ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading artifacts...</p>
          </div>
        ) : filteredArtifacts.length > 0 ? (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Modified At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleArtifacts.map((item) => (
                  <tr key={item.name}>
                    <td>
                      <div className={styles.queueName} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.type === 'directory' ? (
                          <Folder size={16} style={{ color: 'var(--color-text-muted)' }} />
                        ) : (
                          <File size={16} style={{ color: 'var(--color-text-muted)' }} />
                        )}
                        <span
                          style={{ cursor: 'pointer', color: 'var(--color-text-primary)' }}
                          onClick={() =>
                            item.type === 'directory' ? handleDirectoryClick(item.name) : handleDownload(item.name)
                          }>
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.itemSize}>{item.type === 'file' ? formatSize(item.size) : '-'}</span>
                    </td>
                    <td>
                      <span className={styles.itemDate}>{dayjs(item.mtime).format('MMM D, YYYY HH:mm')}</span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {item.type === 'file' && (
                          <button
                            className={styles.actionButton}
                            onClick={() => handleDownload(item.name)}
                            title="Download">
                            <Download size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div style={{ padding: '12px', textAlign: 'center' }}>
                <button className={styles.refreshButton} onClick={() => setDisplayLimit((prev) => prev + 10)}>
                  Show More (+10)
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyRow}>
            <Search size={32} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.5 }} />
            <p>{searchQuery ? 'No artifacts match your search' : 'No artifacts found in this directory'}</p>
            {currentPath !== '' && (
              <button
                className={styles.refreshButton}
                style={{ marginTop: '10px' }}
                onClick={() => handleBreadcrumbClick(pathParts.length - 2)}>
                <ArrowLeft size={14} />
                <span>Go Back</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Artifacts
